/**
 * Client-side utility function for adding referral parameters to enrollment URLs
 */

import { getCookie } from 'cookies-next';
import { REFERRAL_TOKEN_COOKIE } from './cookies';

const REFERRAL_PAGE_REGEX = /^\/r\/([^/]+)/;

export function isReferralPage(path: string) {
  return REFERRAL_PAGE_REGEX.test(path);
}

/**
 * Get the referral token from URL or cookies
 */
function getReferralToken(): string | null {
  // Check if we're in the browser
  // Detects the browser at runtime; there is no value to parse at a boundary here.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  if (typeof window === 'undefined') {
    return null;
  }

  // First check if we're on a referral page - extract from URL
  const path = window.location.pathname;
  const referralMatch = path.match(REFERRAL_PAGE_REGEX);
  if (referralMatch && referralMatch[1]) {
    return referralMatch[1];
  }

  // Otherwise, check the referral_token cookie
  const cookieValue = getCookie(REFERRAL_TOKEN_COOKIE);
  if (cookieValue) {
    try {
      // The cookie value might be JSON with a referral_token property
      const parsed = JSON.parse(cookieValue as string);
      if (parsed.referral_token) {
        return parsed.referral_token;
      }
      // If it's not JSON or doesn't have referral_token, use the value as-is
      return cookieValue as string;
    } catch {
      // If parsing fails, use the value as-is
      return cookieValue as string;
    }
  }

  // Finally check URL params for ch_rafid
  const searchParams = new URLSearchParams(window.location.search);
  const rafId = searchParams.get('ch_rafid');
  if (rafId) {
    return rafId;
  }

  return null;
}

/**
 * Add referral parameters to enrollment URLs
 */
export function addReferralParamsToUrl(url: string): string {
  // Only add params to URLs containing /enroll
  if (!url.includes('/enroll')) {
    return url;
  }

  const token = getReferralToken();
  if (!token) {
    return url;
  }

  try {
    const urlObj = new URL(url, window.location.origin);
    urlObj.searchParams.set('ad', 'ch_raf');
    urlObj.searchParams.set('ch_rafid', token);

    // Return the URL, keeping it relative if it was relative
    if (url.startsWith('/')) {
      return urlObj.pathname + urlObj.search;
    }
    return urlObj.toString();
  } catch {
    // If URL parsing fails, fall back to simple string concatenation
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}ad=ch_raf&ch_rafid=${token}`;
  }
}
